import type { Root, Code, Parent } from "mdast";
import { visit } from "unist-util-visit";

import { ANNOTATION_OPENING_PATTERN, ANNOTATION_TAG_PATTERN } from "../annotation-tags";

// Models sometimes wrap their entire annotated response in a ``` fence —
// usually unlabeled, but occasionally labeled with `json`, `xml`, `markdown`,
// or another data-format tag. That turns into an mdast `code` leaf whose
// `value` is opaque to rehype-raw, so `<collection>` / `<point_box>` / etc.
// never get parsed as HTML and the annotation pipeline never sees them.
// Unwrap such fences into a raw `html` node so the rest of the pipeline
// (point-box-kebab → track-alias → rehype-raw → annotation processor) runs
// normally.
//
// Two cases, gated differently to avoid eating real code:
//   * Unlabeled fences: unwrap whenever any annotation tag appears. The
//     model picked no language, so there is no "verbatim code" intent to
//     preserve.
//   * Labeled fences: unwrap only when the content *starts* with an
//     annotation opening tag. That covers mis-labeled ```json /``` xml
//     wrappers while leaving genuine ```js / ```python code that merely
//     mentions `<point_box>` in a comment untouched.
export const remarkUnwrapAnnotationCode = () => {
  return (tree: Root) => {
    visit(tree, "code", (node: Code, index, parent) => {
      if (!parent || index == null) return;

      const shouldUnwrap = node.lang
        ? ANNOTATION_OPENING_PATTERN.test(node.value.trimStart())
        : ANNOTATION_TAG_PATTERN.test(node.value);
      if (!shouldUnwrap) return;

      (parent as Parent).children[index] = { type: "html", value: node.value };
    });
  };
};
